import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { newsArticles } from "@/mocks/news";
import { useTranslatedList } from "@/hooks/useTranslatedList";
import Reveal from "@/components/base/Reveal";

export default function NewsArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const foundArticle = newsArticles.find((a) => a.slug === slug);
  const article = useTranslatedList(foundArticle ? [foundArticle] : [], [
    "headline",
    "subtitle",
    "content",
    "category",
  ])[0];
  const related = useTranslatedList(
    newsArticles.filter((a) => a.slug !== slug).slice(0, 3),
    ["headline", "category"],
  );
  const locale = i18n.language === "fr" ? "fr-FR" : "en-US";

  useEffect(() => {
    if (article) {
      document.title = `${article.headline} | L+TV News`;
    }
  }, [article]);

  if (!article) {
    return (
      <div className="pt-20 ltv-container py-16 text-center">
        <i className="ri-error-warning-line text-4xl text-foreground-300 mb-3"></i>
        <h1 className="text-xl font-bold text-foreground-800 mb-2">{t("article.notFound")}</h1>
        <p className="text-foreground-500 mb-4">{t("article.notFoundDesc")}</p>
        <Link to="/news" className="ltv-btn-primary text-sm inline-flex items-center gap-2">
          <i className="ri-arrow-left-line"></i>
          {t("article.backToNews")}
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Article Header — full width */}
      <div className="bg-white border-b border-background-200">
        <div className="ltv-container py-8 md:py-10">
          <Reveal direction="up">
            <div className="max-w-4xl">
              <div className="flex items-center gap-2 mb-3 text-sm text-foreground-500">
                <Link to="/news" className="hover:text-accent-600 transition-colors">
                  {t("article.news")}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span>{article.category}</span>
              </div>
              <span className="bg-accent-500 text-white text-xs font-bold px-2.5 py-1 rounded mb-3 inline-block">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                {article.headline}
              </h1>
              <p className="text-lg md:text-xl text-foreground-600 mb-5">{article.subtitle}</p>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorImage}
                    alt={article.author}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                  <span className="font-medium text-foreground-700">{article.author}</span>
                </div>
                <span>·</span>
                <span>
                  {new Date(article.publishedAt).toLocaleDateString(locale, {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <span>·</span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Featured Image — full width */}
      <div className="ltv-container py-6">
        <Reveal direction="up">
          <div className="rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.headline}
              className="w-full aspect-[21/9] object-cover"
            />
          </div>
        </Reveal>
      </div>

      {/* Article Body — full-width two-column */}
      <div className="ltv-container pb-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2">
            <Reveal direction="up">
              <div className="prose prose-lg max-w-none text-foreground-700 leading-relaxed mb-8">
                <p className="text-lg mb-6">{article.content}</p>
                <p className="mb-4">{t("article.stayTuned")}</p>
                <p className="mb-4">{t("article.moreStories")}</p>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-background-100 text-foreground-600 text-xs font-medium px-3 py-1.5 rounded-md"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Share */}
              <div className="border-t border-background-200 pt-6 mb-8">
                <p className="text-sm font-semibold text-foreground-700 mb-3">
                  {t("article.share")}
                </p>
                <div className="flex gap-2">
                  <a href="#" className="w-9 h-9 flex items-center justify-center rounded-md bg-[#1877F2] text-white text-sm hover:opacity-90 transition-opacity">
                    <i className="ri-facebook-fill"></i>
                  </a>
                  <a href="#" className="w-9 h-9 flex items-center justify-center rounded-md bg-foreground-800 text-white text-sm hover:opacity-90 transition-opacity">
                    <i className="ri-twitter-x-fill"></i>
                  </a>
                  <a href="#" className="w-9 h-9 flex items-center justify-center rounded-md bg-[#25D366] text-white text-sm hover:opacity-90 transition-opacity">
                    <i className="ri-whatsapp-fill"></i>
                  </a>
                  <a href="#" className="w-9 h-9 flex items-center justify-center rounded-md bg-background-200 text-foreground-700 text-sm hover:bg-background-300 transition-colors">
                    <i className="ri-link"></i>
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Sidebar — related stories */}
          <div className="lg:col-span-1">
            <Reveal direction="left" delay={100}>
              <div className="lg:sticky lg:top-24">
                <h3 className="text-lg font-bold text-foreground-900 mb-5">{t("article.related")}</h3>
                <div className="space-y-4">
                  {related.map((r) => (
                    <Link key={r.id} to={`/news/${r.slug}`} className="group flex gap-3">
                      <div className="relative w-28 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={r.image}
                          alt={r.headline}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-800 line-clamp-3 group-hover:text-accent-600 transition-colors">
                          {r.headline}
                        </p>
                        <p className="text-xs text-foreground-500 mt-1">{r.category}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}